import { ShoppingCartOutlined } from '@ant-design/icons';
import { Button, Col, message, Modal, Row } from 'antd';
import Title from 'antd/lib/typography/Title';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { isEmpty } from 'underscore';
import Api from '../../../../api/Api';
import { formatDate } from '../../../../generic/common';
import { useIsMounted, useUserProvider } from '../../../../generic/hooks';
import { StoreContext } from '../../../../store/store';
import { CartTableItem } from '../../../../typings/home';
import {
  ShoppingItem,
  ShoppingItemStatus,
  SummaryPriceItem,
} from '../../../../typings/shopping';
import styles from '../../Style/ShoppingCart.module.scss';
import CartTable from './CartTable';
import { IDatasetBrief } from '../../../../typings/metadata';

export interface CartProp {
  close: () => void;
  checkout: () => void;
}

export default function ShoppingCart(props: CartProp) {
  const ALLOWEDEXTRACTIONTIME = 300000; // 5 minutes
  const isMounted = useIsMounted();
  const [isAuthorized, showAssociatedCompanyWarning] = useUserProvider()
  const { setShoppingItems, showCart, user, setShowAssociateToCompany, datasetBriefs } = useContext(StoreContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false)
  const [pendingExtractions, setPendingExtractions] = useState<boolean>(false);
  const [cartData, setCartData] = useState<CartTableItem[]>([]);
  const [shoppingPrice, setShoppingPrice] = useState<SummaryPriceItem>(
    {} as SummaryPriceItem
  );

  useEffect(() => {
    const isLoggedIn = !isEmpty(user);
    if (!isLoggedIn) {
      setShoppingPrice({} as SummaryPriceItem);
    } else {
      prepareData(false);
    }
  }, [user]);

  const updateTableData = (
    items: { [key: string]: { [key: string]: ShoppingItem } },
    showPending: boolean
  ) => {
    const result: CartTableItem[] = [];
    Object.keys(items).forEach((pointName: string) => {
      const itemsPerPoint = items[pointName];
      Object.keys(itemsPerPoint).forEach((category: string) => {
        const item = itemsPerPoint[category];
        const hasError = item.extractionStatus === 'Error';
        const isPending = item.extractionStatus === 'Pending';
        const brief = datasetBriefs.find((brief: IDatasetBrief) => brief.Id === item.dataset_id)
        const isFwe = brief?.IsFWE
        result.push({
          id: item.id,
          key: item.order_id,
          datasetId: item.dataset_id,
          Dataset: item.dataset_id,
          Location: item.location,
          TimeStr: `${formatDate(moment(item.start_date))} - ${formatDate(
            moment(item.end_date)
          )}`,
          Price: item.price,
          Detail: [],
          showDetail: false,
          Operation: null,
          loading: false,
          isEdit: false,
          pointName: item.point_name,
          orderId: item.order_id,
          category: item.dataset_category,
          acronym: item.dataset_acronym,
          hasError: !showPending
            ? hasError
            : hasError || isPending
              ? true
              : false,
          isPending: isPending,
          isFwe: brief?.IsFWE
        });
      });
    });

    setCartData(result);
  };

  async function prepareData(showPendingItems: boolean) {
    setLoading(true);
    const data = await Api.shopping.GetShoppingCart(true, true);
    setShoppingItems(data.shoppingItems);
    setShoppingPrice({
      totalPrice: data.totalPrice,
      discount: data.discount,
      subtotal: data.subtotal,
    });
    updateTableData(data.shoppingItems, showPendingItems);
    setLoading(false);
  }

  const onDeleteRow = useCallback(async (item: CartTableItem) => {
    setCartData((data) => {
      data.find((c) => c.key === item.key)!.loading = true;
      return [...data];
    });

    setTimeout(async () => {
      const bool = await Api.shopping.DeleteShoppingItem(item.orderId);
      if (bool) {
        prepareData(true);
      } else {
        message.error('Failed to remove!');
        setCartData((data) => {
          data.find((c) => c.key === item.key)!.loading = false;
          return [...data];
        });
      }
    }, 1000);
  }, []);

  // Returns a Promise that resolves after 'ms' Milliseconds
  const timer = (ms: number) => new Promise((res) => setTimeout(res, ms));

  async function getExtractionStatus(elapsedTime: number) {
    if (isMounted) {
      try {
        setLoadingCheckout(true)
        if (elapsedTime >= ALLOWEDEXTRACTIONTIME) {
          // When full extraction times out we should inform the user
          Modal.info({
            title:
              'We are sorry, the data preparation is still pending. Please remove the pending items from the cart and Proceed to Checkout again.',
            content: null,
            centered: true,
            onOk() { },
          });
          setPendingExtractions(false);
          return;
        }
        // We need to prevent the user to checkout failed or pending shopping items
        const data = await Api.shopping.GetShoppingCart(true, true);
        const result: ShoppingItemStatus[] = [];
        Object.keys(data.shoppingItems).forEach((pointName: string) => {
          const itemsPerPoint = data.shoppingItems[pointName];
          Object.keys(itemsPerPoint).forEach((category: string) => {
            const item = itemsPerPoint[category];
            result.push({
              pointName: item.point_name,
              orderId: item.order_id,
              category: item.dataset_category,
              extractionStatus: item.extractionStatus,
            });
          });
        });
        if (isMounted && result.length === 0) {
          Modal.info({
            title:
              'Your shopping cart is empty. Please select at least one item before checkout.',
            content: null,
            centered: true,
            onOk() { },
          });
        } else {
          updateTableData(data.shoppingItems, true);
          const issues = result.filter((item: ShoppingItemStatus) => {
            return item.extractionStatus === 'Error';
          });
          if (issues.length > 0) {
            Modal.info({
              title:
                'We are sorry, the data preparation failed. Please remove the failed items from the cart and Proceed to Checkout again.',
              content: null,
              centered: true,
              onOk() { },
            });
            setPendingExtractions(false);
          } else {
            const pending = result.filter((item: ShoppingItemStatus) => {
              return item.extractionStatus === 'Pending';
            });
            if (pending.length > 0) {
              setPendingExtractions(true);
              await timer(3000);
              getExtractionStatus(elapsedTime + 3000);
            } else {
              setPendingExtractions(false);
              isMounted && props.checkout();
            }
          }
        }

      }catch (e) {
        console.log(e)
      } finally {
        setLoadingCheckout(false)
      }
    }
  }

  const onTriggerCheckout = () => {
    showAssociatedCompanyWarning ?
      setShowAssociateToCompany({
        status: true, fun: () =>
          getExtractionStatus(0)
      }) : getExtractionStatus(0);
  };

  return (
    <Modal
      className={styles.cartModal}
      title="Shopping Cart"
      visible={showCart}
      maskClosable={false}
      footer={null}
      onCancel={props.close}
    >
      <Row gutter={20} className={styles.cartContent}>
        <Col span={18}>
          <Title level={4} style={{ border: 'none' }}>
            Items
          </Title>
          <CartTable
            tableData={cartData}
            delete={onDeleteRow}
            loading={loading}
          />
          <p style={{ margin: '7px 0' }}>
            Note: Metadata, Validations and Analytics are accessible online only
            and will not be provided by email.
          </p>
        </Col>
        <Col span={6}>
          <Title level={4}>Summary</Title>
          <Row style={{ marginTop: '20px' }}>
            <Col span={12} className={styles.sumLabel}>
              Price
            </Col>
            <Col span={12} className={styles.sumPay}>
              <span className={styles.payUnit}>€</span>
              {shoppingPrice.totalPrice}
            </Col>
          </Row>
          <Row>
            <Col span={12} className={styles.sumLabel}>
              Discount
            </Col>
            <Col span={12} className={styles.sumPay}>
              <span className={styles.payUnit}>-€</span>
              {shoppingPrice.discount}
            </Col>
          </Row>
          <Row style={{ marginTop: '35px', fontSize: '18px' }}>
            <Col span={12} className={styles.sumLabel}>
              <b>Total</b>
            </Col>
            <Col span={12} className={styles.sumPay}>
              <b>
                <span className={styles.payUnit}>€</span>
                {shoppingPrice.subtotal}
              </b>
            </Col>
          </Row>

          <Button
            disabled={loading || !cartData || cartData.length === 0}
            className={styles.cartButton}
            onClick={onTriggerCheckout}
            icon={<ShoppingCartOutlined style={{ fontSize: '16px' }} />}
            loading={pendingExtractions || loadingCheckout}
          >
            PROCEED TO CHECKOUT
          </Button>
          {/* </Link>  */}
        </Col>
      </Row>
    </Modal>
  );
}
